import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import vistaarLogo from "assets/img/vistaar-logo.svg";

const PublicNavbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const openAuth = (tab, plan) => {
    window.dispatchEvent(new CustomEvent("openAuthModal", { detail: { tab, plan } }));
  };

  const navLinks = [
    { label: "Features", href: "#features" },
    { label: "Pricing", href: "#pricing" },
    { label: "Reviews", href: "#testimonials" },
    { label: "Contact", href: "#contact" },
  ];

  /* In-page anchors only exist on the landing page. On any other route,
     navigate home first, then scroll to the target section. */
  const handleNavClick = (e, href) => {
    e.preventDefault();
    setMobileOpen(false);
    const id = href.replace("#", "");
    const scrollToSection = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    };
    if (location.pathname === "/") {
      scrollToSection();
    } else {
      navigate("/");
      // Wait for the landing page to mount before scrolling.
      setTimeout(scrollToSection, 350);
    }
  };

  return (
    <header
      className="sticky top-0 z-50 nav-glass"
      style={{ boxShadow: scrolled ? "0 6px 24px rgba(15,23,42,0.06)" : "none", transition: "box-shadow 0.25s ease" }}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-[72px] items-center justify-between">

          {/* Logo + wordmark */}
          <a
            href="#home"
            onClick={(e) => handleNavClick(e, "#home")}
            className="flex items-center gap-2 group"
            aria-label="Vistaar — back to top"
          >
            <div className="flex-shrink-0 transition-transform duration-300 group-hover:scale-105" style={{ height: "48px", width: "48px" }}>
              <img src={vistaarLogo} alt="" width="48" height="48" className="w-full h-full object-contain" />
            </div>
            <span className="brand-wordmark" style={{ transform: "translateY(3px)" }}>Vistaar</span>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1 text-sm">
            {navLinks.map(link => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="px-3 py-2 rounded-lg text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-all font-medium"
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={() => openAuth("login")}
              className="px-3 py-2 rounded-lg text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-all text-sm font-semibold"
            >
              Login
            </button>
            <button onClick={() => openAuth("signup", "free")} className="btn-primary btn-sm">
              Start Free Trial
            </button>
          </div>

          {/* Mobile button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-all text-slate-700"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            aria-controls="mobileMenu"
          >
            {mobileOpen ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18 18 6M6 6l12 12"/>
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"/>
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div id="mobileMenu" className="md:hidden">
          <div className="mx-auto max-w-7xl px-4 py-4 grid gap-1">
            {navLinks.map(link => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="py-2.5 px-4 rounded-xl hover:bg-slate-100 transition text-slate-700 font-medium"
              >
                {link.label}
              </a>
            ))}
            <button
              onClick={() => { setMobileOpen(false); openAuth("login"); }}
              className="mt-2 py-2.5 px-4 rounded-xl hover:bg-slate-100 transition text-slate-700 text-center font-semibold"
            >
              Login
            </button>
            <button onClick={() => { setMobileOpen(false); openAuth("signup", "free"); }} className="mt-1 btn-primary justify-center">
              Start Free Trial
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default PublicNavbar;
